import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Selection } from 'react-aria-components';
import { Plus } from 'lucide-react';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  ListBox,
  ListBoxItem,
  ListBoxItemContent
} from 'v5/design-system/ListBox';
import { SearchSegmentsInput } from 'v5/patterns/filters/SearchSegments';
import { cn } from 'v5/platform/dom/cn';

export interface CustomFieldCatalogItem {
  id: number;
  name: string;
  /** Short label for the field type (e.g. humanised enum). */
  typeLabel: string;
  /** ISO 8601 instant from the API; used to sort the list newest-first. */
  created_at: string;
}

function catalogCreatedAtMs(item: CustomFieldCatalogItem): number {
  const t = Date.parse(item.created_at);
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

const EMPTY_LOCKED_ASSIGNMENT_IDS = new Set<number>();

export interface CustomFieldAddSearchStageProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  catalog: CustomFieldCatalogItem[];
  value: ReadonlySet<number>;
  onChange?: (nextAssignedIds: ReadonlySet<number>) => void;
  /** Already-assigned ids that cannot be unassigned here. */
  lockedAssignmentIds?: ReadonlySet<number>;
  onOpenCreateFromSearchQuery: (prefillName: string) => void;
}

function buildSelectionModel(
  fields: CustomFieldCatalogItem[],
  assignedIds: ReadonlySet<number>
) {
  const fieldIds = new Set(fields.map(field => field.id));
  const selectedKeys = new Set(
    fields
      .filter(field => assignedIds.has(field.id))
      .map(field => String(field.id))
  );
  return { fieldIds, selectedKeys };
}

function toSelectedIds(keys: Selection) {
  if (keys === 'all') return null;
  return new Set(
    [...keys].map(key => Number(String(key))).filter(Number.isFinite)
  );
}

export function CustomFieldAddSearchStage({
  searchQuery,
  onSearchQueryChange,
  catalog,
  value,
  onChange,
  lockedAssignmentIds = EMPTY_LOCKED_ASSIGNMENT_IDS,
  onOpenCreateFromSearchQuery
}: CustomFieldAddSearchStageProps) {
  const { t } = useTranslation('patterns');
  const isInteractive = !!onChange;
  const isAssigned = (fieldId: number) => value.has(fieldId);
  const normalizedQuery = searchQuery.trim();
  const normalizedQueryLower = normalizedQuery.toLowerCase();

  const showCreateFromSearch =
    normalizedQuery.length > 0 &&
    !catalog.some(f => f.name.toLowerCase() === normalizedQueryLower);

  const filtered = useMemo(() => {
    const matches = normalizedQueryLower
      ? catalog.filter(f => f.name.toLowerCase().includes(normalizedQueryLower))
      : [...catalog];
    matches.sort((a, b) => catalogCreatedAtMs(b) - catalogCreatedAtMs(a));
    return matches;
  }, [catalog, normalizedQueryLower]);

  const hasListContent = filtered.length > 0;
  const showEmpty =
    !hasListContent && !showCreateFromSearch && catalog.length === 0;

  const listSelection = useMemo(
    () => buildSelectionModel(filtered, value),
    [filtered, value]
  );

  const handleSelectionChange = (
    keys: Selection,
    fieldIds: ReadonlySet<number>
  ) => {
    if (!onChange) return;
    const nextSelected = toSelectedIds(keys);
    if (!nextSelected) return;
    const nextAssignedIds = new Set(value);
    for (const fieldId of fieldIds) {
      if (nextSelected.has(fieldId)) {
        nextAssignedIds.add(fieldId);
      } else if (!lockedAssignmentIds.has(fieldId)) {
        nextAssignedIds.delete(fieldId);
      }
    }
    for (const id of lockedAssignmentIds) {
      nextAssignedIds.add(id);
    }
    onChange(nextAssignedIds);
  };

  const renderListContent = () => {
    if (showEmpty) {
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-fg-muted">
            {t('CustomFieldAddFlow.noCatalog')}
          </p>
        </div>
      );
    }

    if (!hasListContent && !showCreateFromSearch) {
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-fg-muted">
            {t(
              'CustomFieldAddFlow.noFieldsAvailable',
              normalizedQuery ? { context: 'search' } : undefined
            )}
          </p>
        </div>
      );
    }

    if (!hasListContent) {
      return null;
    }

    return (
      <div className="py-1">
        <p className="px-5 py-2 text-sm font-medium text-fg-muted">
          {t('CustomFieldAddFlow.selectFieldsToAdd')}
        </p>
        <ListBox
          aria-label={t('CustomFieldAddFlow.selectFieldsToAdd')}
          className="py-1 divide-y-0"
          selectionMode="multiple"
          selectionBehavior="toggle"
          selectedKeys={listSelection.selectedKeys}
          onSelectionChange={keys =>
            handleSelectionChange(keys, listSelection.fieldIds)
          }
        >
          {filtered.map(field => (
            <CatalogFieldRow
              key={field.id}
              field={field}
              isAssigned={isAssigned(field.id)}
              isInteractive={isInteractive}
              lockedReason={
                lockedAssignmentIds.has(field.id)
                  ? t('CustomFieldAddFlow.alreadyAdded')
                  : null
              }
            />
          ))}
        </ListBox>
      </div>
    );
  };

  return (
    <>
      <div className="border-b border-border-default px-3 py-3">
        <SearchSegmentsInput
          placeholder={t('CustomFieldAddFlow.searchPlaceholder')}
          value={searchQuery}
          onChange={onSearchQueryChange}
        />
      </div>

      {showCreateFromSearch && (
        <div className="px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenCreateFromSearchQuery(normalizedQuery)}
            className="flex w-full items-center gap-3 px-3 py-2 text-left"
          >
            <Plus className="size-4 shrink-0 text-fg-muted" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {t('CustomFieldAddFlow.createNamed', {
                  name: normalizedQuery
                })}
              </p>
              <p className="text-xs text-fg-muted">
                {t('CustomFieldAddFlow.addAsNewHint')}
              </p>
            </div>
          </Button>
        </div>
      )}

      <div className="max-h-[min(320px,50vh)] overflow-y-auto">
        {renderListContent()}
      </div>
    </>
  );
}

function CatalogFieldRow({
  field,
  isAssigned,
  isInteractive,
  lockedReason
}: {
  field: CustomFieldCatalogItem;
  isAssigned: boolean;
  isInteractive: boolean;
  lockedReason: string | null;
}) {
  const isSelectionLocked = lockedReason !== null;
  const lockedReasonLabel = lockedReason?.trim();
  const isRowDisabled = !isInteractive || isSelectionLocked;
  return (
    <ListBoxItem
      id={String(field.id)}
      textValue={`${field.name} ${field.typeLabel}`}
      isDisabled={isRowDisabled}
      className={cn(
        'flex w-full items-center gap-3 px-5 py-2.5 text-left last:rounded-b-none',
        isRowDisabled && 'opacity-70',
        !isAssigned && 'hover:bg-bg-accent/30',
        isSelectionLocked && 'cursor-default'
      )}
    >
      <ListBoxItemContent
        primary={
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-base font-medium">{field.name}</span>
            <Badge
              variant="gray"
              tone="subtle"
              size="sm"
              className="shrink-0 border border-border-default px-1.5 py-0 text-xs font-normal uppercase tracking-wide"
            >
              {field.typeLabel}
            </Badge>
          </div>
        }
        extra={
          isSelectionLocked && lockedReasonLabel ? (
            <Badge variant="brand" tone="subtle" size="sm">
              {lockedReasonLabel}
            </Badge>
          ) : undefined
        }
      />
    </ListBoxItem>
  );
}
